import React, { type FC, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { GlobalPortal } from '@shared/ui/global-portal';

import { TooltipProps } from './types';

import styles from './styles.module.scss';

/**
 * Описание: Тултип
 * Параметры:
 * parent - элемент, относительно которого появляется тултип
 * onClose - метод. вызывается при закрытии тултипа
 * placing - как позиционировать тултип. возможные значения: top, bottom, left, right(по умолчанию), bottom-corner, top-corner
 */
export const Tooltip: FC<TooltipProps> = ({ parent, onClose, placing = 'right', children }) => {
	const tooltipRef = useRef<HTMLDivElement>(null);
	const triangleRef = useRef<HTMLDivElement>(null);

	// Проверка типа позиции
	const checkPlacing = useCallback((name: string): boolean => placing.includes(name), [placing]);

	// Обновление позиции тултипа
	const updatePosition = useCallback(() => {
		const tooltip = tooltipRef.current;
		const triangle = triangleRef.current;
		const target = parent;
		if (!target || !tooltip || !triangle) return;

		const offset = 12;
		const tooltipRect = tooltip.getBoundingClientRect();
		const parentRect = target.getBoundingClientRect();
		let triangleRect = triangle.getBoundingClientRect();
		const windowHeight = document.documentElement.clientHeight;
		const windowWidth = document.documentElement.clientWidth;

		// ВЫЧИСЛЕНИЕ TOP
		if (checkPlacing('bottom')) {
			let top = window.scrollY + parentRect.top + parentRect.height + offset;
			if (top + tooltipRect.height - window.scrollY < windowHeight) {
				tooltip.style.top = `${top}px`;
				triangle.classList.add(styles.top);
				triangleRect = triangle.getBoundingClientRect();
				triangle.style.top = `${top - triangleRect.height}px`;
			} else if (parentRect.top > tooltipRect.height + offset) {
				top = window.scrollY + parentRect.top - (tooltipRect.height + offset);
				tooltip.style.top = `${top}px`;
				triangle.classList.add(styles.bottom);
				triangleRect = triangle.getBoundingClientRect();
				triangle.style.top = `${top + tooltipRect.height}px`;
			} else {
				top -= top + tooltipRect.height - window.scrollY - windowHeight;
				tooltip.style.top = `${top}px`;
				triangle.style.display = 'none';
			}
		} else if (checkPlacing('top')) {
			let top;
			if (parentRect.top > tooltipRect.height + offset) {
				top = window.scrollY + parentRect.top - (tooltipRect.height + offset);
				tooltip.style.top = `${top}px`;
				triangle.classList.add(styles.bottom);
				triangleRect = triangle.getBoundingClientRect();
				triangle.style.top = `${top + tooltipRect.height}px`;
			} else {
				top = window.scrollY + parentRect.top + parentRect.height + offset;
				if (top + tooltipRect.height - window.scrollY < windowHeight) {
					tooltip.style.top = `${top}px`;
					triangle.classList.add(styles.top);
					triangleRect = triangle.getBoundingClientRect();
					triangle.style.top = `${top - triangleRect.height}px`;
				} else {
					top -= top + tooltipRect.height - window.scrollY - windowHeight;
					tooltip.style.top = `${top}px`;
					triangle.style.display = 'none';
				}
			}
		} else {
			// боковые позиции
			if (tooltipRect.height + offset > windowHeight) {
				tooltip.style.top = `${window.scrollY + offset / 2}px`;
				tooltip.style.height = `${windowHeight - offset}px`;
			} else {
				let top = window.scrollY + parentRect.top + parentRect.height / 2 - tooltipRect.height / 2;
				if (top - window.scrollY < 0) top = window.scrollY + offset / 2;
				else if (top + tooltipRect.height - window.scrollY > windowHeight) {
					top = top - (top + tooltipRect.height - window.scrollY - windowHeight) - offset / 2;
				}
				tooltip.style.top = `${top}px`;
			}
			triangle.style.top = `${window.scrollY + parentRect.top + parentRect.height / 2 - triangleRect.height / 2}px`;
		}

		// ВЫЧИСЛЕНИЕ LEFT
		if (checkPlacing('bottom') || checkPlacing('top') || checkPlacing('corner')) {
			let left = parentRect.left + (parentRect.width / 2 - tooltipRect.width / 2);
			let leftTriangle = parentRect.left + (parentRect.width / 2 - triangleRect.width / 2);
			if (checkPlacing('corner')) {
				left = parentRect.left;
				leftTriangle = parentRect.left + offset;
			}
			if (left + tooltipRect.width + offset > windowWidth) {
				left -= left + tooltipRect.width + offset - windowWidth;
			} else if (left < offset / 2) {
				left = offset / 2;
			}
			tooltip.style.left = `${left}px`;
			triangle.style.left = `${leftTriangle}px`;
		} else if (checkPlacing('left')) {
			let left = parentRect.left - (tooltipRect.width + offset);
			let leftTriangle = left + tooltipRect.width;
			if (parentRect.left > tooltipRect.width) {
				triangle.classList.add(styles.right);
				tooltip.style.left = `${left}px`;
				triangle.style.left = `${leftTriangle}px`;
			} else {
				left = parentRect.left + parentRect.width + offset;
				leftTriangle = left - triangleRect.width;
				if (left + tooltipRect.width < windowWidth) {
					tooltip.style.left = `${left}px`;
					triangle.style.left = `${leftTriangle}px`;
					triangle.classList.add(styles.left);
				} else {
					left = left - (left + tooltipRect.width - windowWidth) - offset / 2;
					tooltip.style.left = `${left}px`;
					triangle.style.display = 'none';
				}
			}
		} else {
			let left = parentRect.left + parentRect.width + offset;
			let leftTriangle = left - triangleRect.width;
			if (left + tooltipRect.width > windowWidth) {
				if (parentRect.left > tooltipRect.width) {
					left = parentRect.left - (tooltipRect.width + offset);
					leftTriangle = left + tooltipRect.width;
					triangle.classList.add(styles.right);
					tooltip.style.left = `${left}px`;
					triangle.style.left = `${leftTriangle}px`;
				} else {
					left = left - (left + tooltipRect.width - windowWidth) - offset / 2;
					tooltip.style.left = `${left}px`;
					triangle.style.display = 'none';
				}
			} else {
				tooltip.style.left = `${left}px`;
				triangle.style.left = `${leftTriangle}px`;
				triangle.classList.add(styles.left);
			}
		}
	}, [parent, checkPlacing]);

	// Обработчик клика вне тултипа
	const onDocumentClick = useCallback(
		(e: MouseEvent) => {
			const tooltip = tooltipRef.current;
			const triangle = triangleRef.current;
			if (!parent || !tooltip || !triangle || !onClose) return;
			const target = e.target as Node;
			if (!tooltip.contains(target) && !triangle.contains(target) && !parent.contains(target)) {
				onClose();
			}
		},
		[parent, onClose]
	);

	// Обработчик ресайза окна
	const onWindowResize = useCallback(() => {
		updatePosition();
	}, [updatePosition]);

	// Добавляем слушатели
	useEffect(() => {
		document.addEventListener('click', onDocumentClick);
		window.addEventListener('resize', onWindowResize);
		return () => {
			document.removeEventListener('click', onDocumentClick);
			window.removeEventListener('resize', onWindowResize);
		};
	}, [onDocumentClick, onWindowResize]);

	// Обновляем позицию при изменении содержимого или родителя
	useLayoutEffect(() => {
		updatePosition();
	}, [updatePosition, children]);

	if (!children) return null;

	return (
		<GlobalPortal>
			<div ref={tooltipRef} className={styles.Tooltip}>
				{children}
			</div>
			<div ref={triangleRef} className={styles.Triangle} />
		</GlobalPortal>
	);
};
